// Definition ID: def:rosta:ledger:paymentprovider:streetwallet
// StreetWallet (Wix) iframe payment provider.
// Implements the postMessage integration described in
// Documentation/Specification/05_ExternalInterfaces/payments/StreetWallet/IFrame-Integration.md

import type { PaymentProvider, PaymentRequest, PaymentResult } from './types';

const DEFAULT_BASE_URL = 'https://streetwalletsa.wixsite.com/website-28/pay';
const DEFAULT_ORIGIN = 'https://streetwalletsa.wixsite.com';
const RESULT_EVENT = 'streetwallet_payment_result';

export interface StreetWalletConfig {
  /** Payment page base URL (path `/pay`). */
  baseUrl?: string;
  /** Origin expected on the postMessage response (security check). */
  allowedOrigin?: string;
  /** Height of the embedded iframe. */
  iframeHeight?: number;
}

/**
 * Resolves a PaymentResult from a StreetWallet iframe embedded in the host element.
 * The promise resolves when the iframe posts `streetwallet_payment_result`, or
 * rejects if no result arrives before `timeoutMs`.
 */
export function createStreetWalletProvider(config: StreetWalletConfig = {}): PaymentProvider {
  const baseUrl = config.baseUrl ?? DEFAULT_BASE_URL;
  const allowedOrigin = config.allowedOrigin ?? DEFAULT_ORIGIN;
  const iframeHeight = config.iframeHeight ?? 600;

  return {
    id: 'streetwallet',
    pay(request: PaymentRequest): Promise<PaymentResult> {
      return new Promise<PaymentResult>((resolve, reject) => {
        const container = document.createElement('div');
        container.dataset.paymentHost = 'streetwallet';
        container.style.position = 'fixed';
        container.style.inset = '0';
        container.style.background = 'rgba(0,0,0,0.6)';
        container.style.zIndex = '99999';
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'center';

        const modal = document.createElement('div');
        modal.style.background = '#fff';
        modal.style.width = '100%';
        modal.style.maxWidth = '480px';
        modal.style.height = `${iframeHeight}px`;
        modal.style.borderRadius = '12px';
        modal.style.overflow = 'hidden';
        modal.style.position = 'relative';

        const close = document.createElement('button');
        close.textContent = '×';
        close.setAttribute('aria-label', 'Close payment');
        close.style.position = 'absolute';
        close.style.top = '8px';
        close.style.right = '8px';
        close.style.zIndex = '2';
        close.style.border = 'none';
        close.style.background = 'transparent';
        close.style.fontSize = '1.5rem';
        close.style.cursor = 'pointer';
        close.onclick = () => cleanup(new Error('cancelled'));

        const iframe = document.createElement('iframe');
        const url = new URL(baseUrl);
        url.searchParams.set('reference', request.reference);
        url.searchParams.set('amount', String(request.amount));
        iframe.src = url.toString();
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';

        modal.appendChild(close);
        modal.appendChild(iframe);
        container.appendChild(modal);
        document.body.appendChild(container);

        const onMessage = (event: MessageEvent) => {
          if (event.origin !== allowedOrigin) return;
          const data = event.data as
            | { event?: string; status?: string; reference?: string; amount?: number }
            | undefined;
          if (!data || data.event !== RESULT_EVENT) return;
          cleanup();
          resolve({
            status: (data.status as PaymentResult['status']) ?? 'failed',
            reference: data.reference ?? request.reference,
            amount: data.amount ?? request.amount,
            raw: data,
          });
        };

        const cleanup = (err?: Error) => {
          window.removeEventListener('message', onMessage);
          container.remove();
          if (err) reject(err);
        };

        window.addEventListener('message', onMessage);
      });
    },
  };
}
